import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MediaKind,
  MovieSummary,
  QualityProfileDTO,
  SeriesSummary,
  TmdbSearchResult,
} from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { Button, Card, Field, Input, Spinner } from '../components/ui';

type MonitorEpisodes = 'all' | 'future' | 'none';

const selectClass =
  'w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-indigo-500';

export function Add() {
  const [kind, setKind] = useState<MediaKind>('movie');
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [profileId, setProfileId] = useState<string>('');
  const [monitorEpisodes, setMonitorEpisodes] = useState<MonitorEpisodes>('all');
  const [searchNow, setSearchNow] = useState(false);
  const [err, setErr] = useState('');

  const { data: profiles } = useQuery({
    queryKey: ['quality-profiles'],
    queryFn: () => api.get<{ profiles: QualityProfileDTO[] }>('/api/quality-profiles'),
  });

  const search = useQuery({
    queryKey: ['tmdb-search', kind, submitted],
    queryFn: () =>
      api.get<{ results: TmdbSearchResult[] }>(
        `/api/search/tmdb?kind=${kind}&q=${encodeURIComponent(submitted)}`,
      ),
    enabled: submitted.length > 0,
  });

  const add = useMutation({
    mutationFn: (r: TmdbSearchResult): Promise<{ id: string }> => {
      const qualityProfileId = profileId || undefined;
      if (r.kind === 'movie') {
        return api.post<MovieSummary>('/api/movies', {
          tmdbId: r.tmdbId,
          monitored: true,
          qualityProfileId,
          searchNow,
        });
      }
      return api.post<SeriesSummary>('/api/series', {
        tmdbId: r.tmdbId,
        monitored: true,
        qualityProfileId,
        monitorEpisodes,
      });
    },
    // Stay on the search results so you can keep adding; just mark this one as added.
    onSuccess: (_item, r) => setAdded((prev) => new Set(prev).add(`${r.kind}:${r.tmdbId}`)),
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed to add'),
  });

  const switchKind = (k: MediaKind) => {
    setKind(k);
    setSubmitted('');
    setErr('');
  };

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold">Add</h1>

      <div className="flex gap-2">
        <KindButton active={kind === 'movie'} onClick={() => switchKind('movie')}>
          Movie
        </KindButton>
        <KindButton active={kind === 'series'} onClick={() => switchKind('series')}>
          TV
        </KindButton>
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setErr('');
          setSubmitted(q.trim());
        }}
      >
        <Input
          placeholder={kind === 'movie' ? 'Search TMDB for a movie…' : 'Search TMDB for a TV show…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <Button type="submit">Search</Button>
      </form>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Quality profile">
          <select className={selectClass} value={profileId} onChange={(e) => setProfileId(e.target.value)}>
            <option value="">Default</option>
            {profiles?.profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {kind === 'series' ? (
          <Field label="Monitor episodes">
            <select
              className={selectClass}
              value={monitorEpisodes}
              onChange={(e) => setMonitorEpisodes(e.target.value as MonitorEpisodes)}
            >
              <option value="all">All</option>
              <option value="future">Future only</option>
              <option value="none">None</option>
            </select>
          </Field>
        ) : (
          <Field label="Search now">
            <label className="flex h-9 items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={searchNow} onChange={(e) => setSearchNow(e.target.checked)} />
              Kick off a search immediately
            </label>
          </Field>
        )}
      </div>

      {err && <p className="text-sm text-rose-400">{err}</p>}
      {search.isFetching && <Spinner label="Searching TMDB…" />}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {search.data?.results.map((r) => {
          const key = `${r.kind}:${r.tmdbId}`;
          const isAdded = added.has(key);
          const isPending =
            add.isPending && add.variables?.tmdbId === r.tmdbId && add.variables?.kind === r.kind;
          return (
            <Card key={key} className="flex gap-3">
              <div className="h-36 w-24 flex-shrink-0 overflow-hidden rounded bg-slate-800">
                {r.posterUrl ? (
                  <img src={r.posterUrl} alt={r.title} loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-slate-500">
                    No poster
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="font-medium text-slate-100">
                  {r.title} {r.year != null && <span className="text-slate-400">({r.year})</span>}
                </div>
                <p className="mt-1 line-clamp-4 flex-1 text-xs text-slate-400">{r.overview ?? 'No overview.'}</p>
                <div className="mt-2">
                  <Button
                    variant={isAdded ? 'ghost' : 'subtle'}
                    disabled={isPending || isAdded}
                    onClick={() => {
                      setErr('');
                      add.mutate(r);
                    }}
                  >
                    {isAdded ? 'Added ✓' : isPending ? 'Adding…' : 'Add & monitor'}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {search.data && search.data.results.length === 0 && (
          <p className="text-sm text-slate-400">No results found.</p>
        )}
      </div>
    </div>
  );
}

function KindButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-md bg-indigo-600/20 px-4 py-1.5 text-sm text-indigo-200'
          : 'rounded-md border border-slate-700 px-4 py-1.5 text-sm text-slate-300 hover:bg-slate-800'
      }
    >
      {children}
    </button>
  );
}
